import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import SEO, { SEO_CONFIG } from '@/components/SEO';
import { 
  Bot, Send, User, Loader2, Trash2, Plus, MessageSquare, 
  Crown, TrendingUp, DollarSign, BookOpen, Sparkles,
  ChevronRight, History
} from 'lucide-react';
import { toast } from 'sonner';
import API from '@/config/api';

const suggestedQuestions = [
  { icon: TrendingUp, text: "How do I start day trading?", category: "Trading" },
  { icon: DollarSign, text: "What's the best way to build credit?", category: "Credit" },
  { icon: BookOpen, text: "Explain support and resistance levels", category: "Technical" },
  { icon: Crown, text: "How can I build generational wealth?", category: "Wealth" },
];

export default function AIAssistant() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [showSessions, setShowSessions] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Load sessions on mount
  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    try {
      const response = await fetch(`${API}/ai-assistant/sessions`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setSessions(data.sessions || []);
      }
    } catch (error) {
      console.error('Failed to load sessions:', error);
    }
  };

  const loadSession = async (sid) => {
    try {
      const response = await fetch(`${API}/ai-assistant/history?session_id=${sid}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        const loadedMessages = data.conversations.flatMap(conv => [
          { role: 'user', content: conv.user_message },
          { role: 'assistant', content: conv.ai_response }
        ]);
        setMessages(loadedMessages);
        setSessionId(sid);
        setShowSessions(false);
      }
    } catch (error) {
      console.error('Failed to load session:', error);
      toast.error('Failed to load conversation');
    }
  };

  const startNewSession = () => {
    setMessages([]);
    setSessionId(null);
    setShowSessions(false);
    inputRef.current?.focus();
  };

  const deleteSession = async (sid, e) => {
    e.stopPropagation();
    try {
      const response = await fetch(`${API}/ai-assistant/session/${sid}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) {
        setSessions(sessions.filter(s => s.session_id !== sid));
        if (sessionId === sid) {
          startNewSession();
        }
        toast.success('Conversation deleted');
      }
    } catch (error) {
      console.error('Failed to delete session:', error);
      toast.error('Failed to delete conversation');
    }
  };

  const sendMessage = async (messageText = input) => {
    if (!messageText.trim() || loading) return;

    const userMessage = { role: 'user', content: messageText.trim() };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await fetch(`${API}/ai-assistant/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          message: messageText.trim(),
          session_id: sessionId
        })
      });

      if (!response.ok) {
        throw new Error('Failed to get response');
      }

      const data = await response.json();
      
      // Update session ID if new
      if (!sessionId && data.session_id) {
        setSessionId(data.session_id);
        loadSessions(); // Refresh sessions list
      }

      const assistantMessage = { role: 'assistant', content: data.response };
      setMessages(prev => [...prev, assistantMessage]);

    } catch (error) {
      console.error('Chat error:', error);
      toast.error('Failed to send message. Please try again.');
      // Remove the user message if failed
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white pt-16" data-testid="ai-assistant-page">
      <SEO {...SEO_CONFIG.aiAssistant} />
      
      <div className="max-w-6xl mx-auto p-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-[#D4AF37] to-[#B8962E] rounded-xl flex items-center justify-center">
              <Bot className="h-7 w-7 text-black" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">AI Trade Assistant</h1>
              <p className="text-gray-400 text-sm">Your personal wealth-building advisor</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowSessions(!showSessions)}
              className="border-[#1c1814] text-gray-300 hover:bg-[#1c1814]"
            >
              <History className="h-4 w-4 mr-2" />
              History
            </Button>
            <Button
              onClick={startNewSession}
              size="sm"
              className="bg-[#D4AF37] text-black hover:bg-[#B8962E]"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Chat
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Sessions Sidebar (mobile: overlay, desktop: sidebar) */}
          {showSessions && (
            <Card className="lg:col-span-1 bg-[#14120f] border-[#1c1814]">
              <CardHeader className="border-b border-[#1c1814] py-3">
                <CardTitle className="text-sm text-gray-400">Recent Conversations</CardTitle>
              </CardHeader>
              <CardContent className="p-2">
                <ScrollArea className="h-[400px]">
                  {sessions.length === 0 ? (
                    <p className="text-gray-500 text-sm text-center py-4">No conversations yet</p>
                  ) : (
                    <div className="space-y-1">
                      {sessions.map((session) => (
                        <div
                          key={session.session_id}
                          onClick={() => loadSession(session.session_id)}
                          className={`p-3 rounded-lg cursor-pointer transition-colors group ${
                            sessionId === session.session_id 
                              ? 'bg-[#D4AF37]/20 border border-[#D4AF37]/30' 
                              : 'hover:bg-[#1c1814]'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-white truncate">{session.preview}</p>
                              <p className="text-xs text-gray-500 mt-1">
                                {session.message_count} messages
                              </p>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => deleteSession(session.session_id, e)}
                              className="opacity-0 group-hover:opacity-100 h-6 w-6 p-0 text-gray-400 hover:text-red-400"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          )}

          {/* Main Chat Area */}
          <Card className={`${showSessions ? 'lg:col-span-3' : 'lg:col-span-4'} bg-[#14120f] border-[#1c1814]`}>
            <CardContent className="p-0 flex flex-col h-[600px]">
              {/* Messages Area */}
              <ScrollArea className="flex-1 p-4">
                {messages.length === 0 ? (
                  <div className="h-full flex flex-col items-center justify-center text-center py-8">
                    <div className="w-20 h-20 bg-gradient-to-br from-[#D4AF37]/20 to-[#D4AF37]/5 rounded-2xl flex items-center justify-center mb-4">
                      <Sparkles className="h-10 w-10 text-[#D4AF37]" />
                    </div>
                    <h2 className="text-xl font-bold text-white mb-2">Welcome to AI Trade Assistant</h2>
                    <p className="text-gray-400 max-w-md mb-6">
                      Ask me anything about trading, investing, credit building, or wealth management. I'm here to help you build your financial legacy.
                    </p>
                    
                    {/* Suggested Questions */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full max-w-lg">
                      {suggestedQuestions.map((q, i) => (
                        <button
                          key={i}
                          onClick={() => sendMessage(q.text)}
                          className="flex items-center gap-3 p-3 bg-[#0a0a0f] border border-[#1c1814] rounded-lg hover:border-[#D4AF37]/50 transition-colors text-left group"
                        >
                          <div className="w-8 h-8 bg-[#1c1814] rounded-lg flex items-center justify-center group-hover:bg-[#D4AF37]/20">
                            <q.icon className="h-4 w-4 text-[#D4AF37]" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-white truncate">{q.text}</p>
                            <p className="text-xs text-gray-500">{q.category}</p>
                          </div>
                          <ChevronRight className="h-4 w-4 text-gray-500 group-hover:text-[#D4AF37]" />
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {messages.map((message, index) => (
                      <div
                        key={index}
                        className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        {message.role === 'assistant' && (
                          <div className="w-8 h-8 bg-gradient-to-br from-[#D4AF37] to-[#B8962E] rounded-lg flex items-center justify-center flex-shrink-0">
                            <Bot className="h-4 w-4 text-black" />
                          </div>
                        )}
                        <div
                          className={`max-w-[80%] p-4 rounded-2xl ${
                            message.role === 'user'
                              ? 'bg-[#D4AF37] text-black rounded-br-md'
                              : 'bg-[#1c1814] text-white rounded-bl-md'
                          }`}
                        >
                          <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                        </div>
                        {message.role === 'user' && (
                          <div className="w-8 h-8 bg-[#2d2722] rounded-lg flex items-center justify-center flex-shrink-0">
                            <User className="h-4 w-4 text-gray-400" />
                          </div>
                        )}
                      </div>
                    ))}
                    {loading && (
                      <div className="flex gap-3 justify-start">
                        <div className="w-8 h-8 bg-gradient-to-br from-[#D4AF37] to-[#B8962E] rounded-lg flex items-center justify-center flex-shrink-0">
                          <Bot className="h-4 w-4 text-black" />
                        </div>
                        <div className="bg-[#1c1814] p-4 rounded-2xl rounded-bl-md">
                          <div className="flex items-center gap-2">
                            <Loader2 className="h-4 w-4 animate-spin text-[#D4AF37]" />
                            <span className="text-sm text-gray-400">Thinking...</span>
                          </div>
                        </div>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </ScrollArea>

              {/* Input Area */}
              <div className="p-4 border-t border-[#1c1814]">
                <div className="flex gap-2">
                  <Input
                    ref={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Ask about trading, investing, credit building..."
                    className="flex-1 bg-[#0a0a0f] border-[#1c1814] text-white placeholder:text-gray-500 focus:border-[#D4AF37]"
                    disabled={loading}
                    data-testid="ai-chat-input"
                  />
                  <Button
                    onClick={() => sendMessage()}
                    disabled={!input.trim() || loading}
                    className="bg-[#D4AF37] text-black hover:bg-[#B8962E] px-6"
                    data-testid="ai-chat-send"
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-2 text-center">
                  AI responses are for educational purposes only. Always do your own research before making financial decisions.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
